import { get, del, post, put } from '../api.js';
import { encryptItem } from '../crypto.js';
import { navigate } from '../router.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { relTime } from '../utils.js';
import { toast, confirmDialog, promptDialog } from '../dialog.js';
import { enqueueFiles, onAllComplete, clearCompleteCallbacks, getLastCreatedGroupId } from '../upload-queue.js';
import { setupFileDropZone, setupItemDragSource, setupItemDropTarget } from '../drop-handler.js';
import { decryptItemEnvelope, decryptGroupName } from '../item-decrypt.js';

function getViewParam() {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  if (qIdx < 0) return { view: 'items', groupId: null };
  const params = new URLSearchParams(hash.slice(qIdx + 1));
  return { view: params.get('view') || 'items', groupId: params.get('group') };
}

export async function renderItemsList(container) {
  const { view, groupId } = getViewParam();

  container.innerHTML = '<p class="loading">Loading...</p>';

  try {
    const [items, groups, notarizations, will] = await Promise.all([
      get('/items'),
      get('/groups'),
      get('/notarizations').catch(() => []),
      get('/will').catch(() => null),
    ]);

    // Decrypt group names
    const groupsDisplay = groups.map(g => ({ ...g, name: decryptGroupName(g) }));

    // Build item→groups map by fetching each group's items
    const groupItemsMap = {}; // groupId → [itemId]
    const itemGroupsMap = {}; // itemId → [{id, name}]
    await Promise.all(groupsDisplay.map(async (g) => {
      const gItems = await get(`/groups/${g.id}/items`).catch(() => []);
      groupItemsMap[g.id] = gItems.map(i => i.id);
      for (const gi of gItems) {
        if (!itemGroupsMap[gi.id]) itemGroupsMap[gi.id] = [];
        itemGroupsMap[gi.id].push({ id: g.id, name: g.name });
      }
    }));

    // Add item counts to groups
    groupsDisplay.forEach(g => g.itemCount = (groupItemsMap[g.id] || []).length);

    const notarizedItemIds = new Set(notarizations.filter(n => n.item_id).map(n => n.item_id));
    const willItemIds = new Set(will && Array.isArray(will.wrapped_items) ? will.wrapped_items.map(w => w.item_id) : []);

    const displayItems = items.map(item => {
      const { envelope: env } = decryptItemEnvelope(item);
      return {
        id: item.id,
        name: env?.name || 'Untitled',
        item_type: env?.item_type || item.item_type,
        isFile: env ? !!env.filename : false,
        isInbox: item.source === 'inbox',
        created_at: item.created_at,
        notarized: notarizedItemIds.has(item.id),
        blob_hash: item.blob_hash,
        inWill: willItemIds.has(item.id),
        groups: itemGroupsMap[item.id] || [],
      };
    });

    // Drill-in to a specific collection
    if (view === 'collections' && groupId) {
      renderDrillIn(container, groupId, groupsDisplay, displayItems, groupItemsMap, itemGroupsMap);
      return;
    }

    // Context-aware topbar
    const isCollections = view === 'collections';
    const actionBtn = isCollections
      ? '<button class="btn btn-primary btn-sm" id="new-collection-btn"><i data-lucide="plus" style="width:16px;height:16px"></i> New Collection</button>'
      : '<a href="#/items/new" class="btn btn-primary btn-sm"><i data-lucide="plus" style="width:16px;height:16px"></i> New Item</a>';
    setTopbar('Items', actionBtn);

    // Segmented control + view
    let html = `<div class="segmented-control">
      <button class="seg-btn ${!isCollections ? 'active' : ''}" data-seg="items">Items</button>
      <button class="seg-btn ${isCollections ? 'active' : ''}" data-seg="collections">Collections</button>
    </div>`;

    if (isCollections) {
      html += renderCollectionsView(groupsDisplay);
    } else {
      html += renderItemsView(displayItems, groupsDisplay);
    }

    container.innerHTML = html;
    lucideIcons();

    // Segmented control toggle
    container.querySelectorAll('.seg-btn').forEach(btn => {
      btn.onclick = () => {
        const seg = btn.dataset.seg;
        if (seg === 'collections') navigate('#/items?view=collections');
        else navigate('#/items');
      };
    });

    if (isCollections) {
      bindCollectionsView(container, groupsDisplay, displayItems, groupItemsMap, itemGroupsMap);
    } else {
      bindItemsView(container, displayItems, groupsDisplay, groupItemsMap, itemGroupsMap);
    }

    // New Collection button
    const newColBtn = document.getElementById('new-collection-btn');
    if (newColBtn) {
      newColBtn.onclick = () => createNewCollection(container);
    }
  } catch (err) {
    container.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
  }
}

// ── Items View ──

function renderItemsView(displayItems, groupsDisplay) {
  let html = `<div style="display:flex;gap:10px;align-items:center;margin-bottom:20px">
    <div class="search-bar" style="flex:1;margin-bottom:0"><i data-lucide="search"></i><input placeholder="Search items..." id="items-search"></div>
    <select class="form-input filter-dropdown" id="group-filter" style="width:auto;min-width:140px">
      <option value="">All Items</option>
      <option value="ungrouped">Ungrouped</option>
      ${groupsDisplay.map(g => `<option value="${g.id}">${esc(g.name)}</option>`).join('')}
    </select>
    <button class="btn btn-secondary btn-sm" id="select-toggle" style="white-space:nowrap"><i data-lucide="check-square" style="width:14px;height:14px"></i> Select</button>
  </div>`;

  if (!displayItems.length) {
    html += `<div class="empty-state">
      <div class="empty-icon"><i data-lucide="lock" style="width:28px;height:28px;color:var(--text-secondary)"></i></div>
      <h3>Your vault is empty</h3>
      <p>Add your first item using the button below, or <a href="#/items/new" style="color:var(--accent)">create a text secret</a>.</p>
    </div>
    <div class="items-grid" id="items-grid">
      <div class="card item-card ghost-tile" id="upload-ghost-tile">
        <div class="collection-icon ghost"><i data-lucide="plus" style="width:28px;height:28px"></i></div>
        <div class="item-name drop-desktop-hint">Drop files here</div>
        <label for="ghost-file-input" class="item-name drop-mobile-btn" style="display:none;cursor:pointer">Add Files</label>
        <input type="file" id="ghost-file-input" multiple style="display:none">
      </div>
    </div>`;
    return html;
  }

  html += '<div class="items-grid" id="items-grid">';
  displayItems.forEach(item => {
    html += renderItemCard(item);
  });
  html += `
    <div class="card item-card ghost-tile" id="upload-ghost-tile">
      <div class="collection-icon ghost"><i data-lucide="plus" style="width:28px;height:28px"></i></div>
      <div class="item-name drop-desktop-hint">Drop files here</div>
      <label for="ghost-file-input" class="item-name drop-mobile-btn" style="display:none;cursor:pointer">Add Files</label>
      <input type="file" id="ghost-file-input" multiple style="display:none">
    </div>`;
  html += '</div>';
  html += `<div class="multi-select-bar" id="multi-select-bar" style="display:none">
    <span id="select-count">0 selected</span>
    <button class="btn btn-primary btn-sm" id="bulk-add-collection"><i data-lucide="folder-plus" style="width:14px;height:14px"></i> Add to Collection</button>
    <button class="btn btn-danger btn-sm" id="bulk-delete"><i data-lucide="trash-2" style="width:14px;height:14px"></i> Delete</button>
    <button class="btn btn-secondary btn-sm" id="select-cancel">Cancel</button>
  </div>`;
  return html;
}

function renderItemCard(item) {
  const iconType = item.isInbox ? 'drop' : item.isFile ? 'drop' : 'encrypted';
  const badgeClass = item.isInbox ? 'badge-drop' : item.isFile ? 'badge-drop' : 'badge-encrypted';
  const badgeText = item.isInbox ? 'Inbox' : item.isFile ? 'File' : 'Encrypted';
  const iconName = item.isInbox ? 'mail' : item.isFile ? 'inbox' : 'lock';
  const groupBadges = item.groups.map(g =>
    `<span class="group-badge">${esc(g.name)}</span>`
  ).join('');

  return `
    <div class="card item-card" data-id="${item.id}" data-groups="${item.groups.map(g => g.id).join(',')}">
      <div class="item-select" style="display:none"><input type="checkbox" class="item-checkbox" data-id="${item.id}"></div>
      <div class="item-actions">
        <button data-action="share" data-id="${item.id}"><i data-lucide="share-2" style="width:16px;height:16px"></i></button>
        <button class="del" data-action="delete" data-id="${item.id}"><i data-lucide="trash-2" style="width:16px;height:16px"></i></button>
      </div>
      <div class="item-icon ${iconType}"><i data-lucide="${iconName}" style="width:22px;height:22px"></i></div>
      <div class="item-name">${esc(item.name)}</div>
      ${groupBadges ? `<div class="group-badges">${groupBadges}</div>` : ''}
      <div class="item-meta"><span class="badge ${badgeClass}">${badgeText}</span>${item.notarized ? `<span class="badge" style="background:var(--bg-elevated);color:var(--accent);font-size:10px"><i data-lucide="stamp" style="width:10px;height:10px"></i> Notarized${item.blob_hash ? ' <i data-lucide="shield-check" style="width:9px;height:9px"></i>' : ''}</span>` : ''}${item.inWill ? '<span class="badge" style="background:var(--bg-elevated);color:var(--warning, #e6a817);font-size:10px"><i data-lucide="scroll-text" style="width:10px;height:10px"></i> Heir</span>' : ''}<span>${relTime(item.created_at)}</span></div>
    </div>`;
}

function bindItemsView(container, displayItems, groupsDisplay, groupItemsMap, itemGroupsMap) {
  let selectMode = false;

  // File drop zone on items grid (or container if empty)
  const dropTarget = container.querySelector('#items-grid') || container;
  setupFileDropZone(dropTarget, (files) => enqueueFiles(files, {}));

  // Ghost tile — file upload on click/tap
  const ghostTile = document.getElementById('upload-ghost-tile');
  const ghostFileInput = document.getElementById('ghost-file-input');
  if (ghostTile && ghostFileInput) {
    setupFileDropZone(ghostTile, (files) => enqueueFiles(files, {}));
    ghostTile.addEventListener('click', (e) => {
      if (e.target === ghostFileInput || e.target.closest('.drop-mobile-btn')) return;
      ghostFileInput.click();
    });
    ghostFileInput.addEventListener('change', () => {
      if (ghostFileInput.files.length) enqueueFiles(ghostFileInput.files, {});
    });
  }

  // Make item cards draggable
  container.querySelectorAll('.item-card:not(.ghost-tile)').forEach(card => {
    setupItemDragSource(card, card.dataset.id);
  });

  // Refresh when uploads complete
  clearCompleteCallbacks();
  onAllComplete(() => {
    if (window.location.hash.startsWith('#/items')) renderItemsList(container);
  });

  // Search
  const searchInput = document.getElementById('items-search');
  if (searchInput) {
    searchInput.oninput = () => filterItems(container);
  }

  // Group filter
  const filterSelect = document.getElementById('group-filter');
  if (filterSelect) {
    filterSelect.onchange = () => filterItems(container);
  }

  function filterItems() {
    const q = (document.getElementById('items-search')?.value || '').toLowerCase();
    const gf = document.getElementById('group-filter')?.value || '';
    container.querySelectorAll('.item-card').forEach(c => {
      const name = c.querySelector('.item-name').textContent.toLowerCase();
      const matchSearch = !q || name.includes(q);
      let matchGroup = true;
      if (gf === 'ungrouped') {
        matchGroup = !c.dataset.groups;
      } else if (gf) {
        matchGroup = c.dataset.groups.split(',').includes(gf);
      }
      c.style.display = (matchSearch && matchGroup) ? '' : 'none';
    });
  }

  // Card clicks
  container.querySelectorAll('.item-card:not(.ghost-tile)').forEach(card => {
    card.onclick = (e) => {
      if (selectMode) {
        const cb = card.querySelector('.item-checkbox');
        if (cb && !e.target.closest('.item-checkbox')) cb.checked = !cb.checked;
        updateSelectCount();
        return;
      }
      const actionBtn = e.target.closest('[data-action]');
      if (actionBtn) {
        e.stopPropagation();
        const action = actionBtn.dataset.action;
        const id = actionBtn.dataset.id;
        if (action === 'share') navigate(`#/grants/new?item_id=${id}`);
        if (action === 'delete') deleteItem(id, container);
        return;
      }
      navigate(`#/items/${card.dataset.id}`);
    };
  });

  // Select mode toggle
  const selectToggle = document.getElementById('select-toggle');
  const selectBar = document.getElementById('multi-select-bar');
  if (selectToggle) {
    selectToggle.onclick = () => {
      selectMode = !selectMode;
      container.querySelectorAll('.item-select').forEach(s => s.style.display = selectMode ? '' : 'none');
      container.querySelectorAll('.item-actions').forEach(a => a.style.display = selectMode ? 'none' : '');
      if (selectBar) selectBar.style.display = selectMode ? '' : 'none';
      if (!selectMode) {
        container.querySelectorAll('.item-checkbox').forEach(cb => cb.checked = false);
      }
      selectToggle.classList.toggle('btn-primary', selectMode);
      selectToggle.classList.toggle('btn-secondary', !selectMode);
    };
  }

  function updateSelectCount() {
    const count = container.querySelectorAll('.item-checkbox:checked').length;
    const el = document.getElementById('select-count');
    if (el) el.textContent = `${count} selected`;
  }

  container.addEventListener('change', (e) => {
    if (e.target.classList.contains('item-checkbox')) updateSelectCount();
  });

  // Cancel select
  const cancelBtn = document.getElementById('select-cancel');
  if (cancelBtn) {
    cancelBtn.onclick = () => {
      selectMode = false;
      container.querySelectorAll('.item-select').forEach(s => s.style.display = 'none');
      container.querySelectorAll('.item-actions').forEach(a => a.style.display = '');
      if (selectBar) selectBar.style.display = 'none';
      container.querySelectorAll('.item-checkbox').forEach(cb => cb.checked = false);
      selectToggle.classList.remove('btn-primary');
      selectToggle.classList.add('btn-secondary');
    };
  }

  // Bulk add to collection
  const bulkAddBtn = document.getElementById('bulk-add-collection');
  if (bulkAddBtn) {
    bulkAddBtn.onclick = async () => {
      const selectedIds = Array.from(container.querySelectorAll('.item-checkbox:checked')).map(cb => cb.dataset.id);
      if (!selectedIds.length) { toast('Select at least one item'); return; }
      await showCollectionPicker(selectedIds, groupsDisplay, container);
    };
  }

  // Bulk delete
  const bulkDeleteBtn = document.getElementById('bulk-delete');
  if (bulkDeleteBtn) {
    bulkDeleteBtn.onclick = async () => {
      const selectedIds = Array.from(container.querySelectorAll('.item-checkbox:checked')).map(cb => cb.dataset.id);
      if (!selectedIds.length) { toast('Select at least one item'); return; }
      if (!await confirmDialog(`Delete ${selectedIds.length} item${selectedIds.length !== 1 ? 's' : ''}?`, { danger: true })) return;
      try {
        for (const id of selectedIds) await del(`/items/${id}`);
        toast(`Deleted ${selectedIds.length} item${selectedIds.length !== 1 ? 's' : ''}`, 'success');
        renderItemsList(container);
      } catch (err) { toast(err.message); }
    };
  }
}

// ── Collections View ──

function renderCollectionsView(groupsDisplay) {
  let html = `<div class="search-bar" style="margin-bottom:20px"><i data-lucide="search"></i><input placeholder="Search collections..." id="collections-search"></div>`;

  html += '<div class="items-grid" id="collections-grid">';
  groupsDisplay.forEach(group => {
    html += `
      <div class="card collection-tile" data-id="${group.id}">
        <div class="item-actions">
          <button data-action="share" data-id="${group.id}"><i data-lucide="share-2" style="width:16px;height:16px"></i></button>
          <button class="del" data-action="delete" data-id="${group.id}"><i data-lucide="trash-2" style="width:16px;height:16px"></i></button>
        </div>
        <div class="collection-icon"><i data-lucide="folder" style="width:28px;height:28px"></i></div>
        <div class="item-name">${esc(group.name)}</div>
        <div class="item-meta"><span class="badge badge-encrypted">${group.itemCount} item${group.itemCount !== 1 ? 's' : ''}</span><span>${relTime(group.created_at)}</span></div>
      </div>`;
  });
  // Ghost tile for "drop to create new collection"
  html += `
    <div class="card collection-tile ghost-tile" data-action="new-collection-drop">
      <div class="collection-icon ghost"><i data-lucide="plus" style="width:28px;height:28px"></i></div>
      <div class="item-name">New Collection</div>
      <div class="item-meta">Drop files here</div>
    </div>`;
  html += '</div>';
  return html;
}

function bindCollectionsView(container, groupsDisplay, displayItems, groupItemsMap, itemGroupsMap) {
  // Search
  const searchInput = document.getElementById('collections-search');
  if (searchInput) {
    searchInput.oninput = (e) => {
      const q = e.target.value.toLowerCase();
      container.querySelectorAll('.collection-tile:not(.ghost-tile)').forEach(c => {
        const name = c.querySelector('.item-name').textContent.toLowerCase();
        c.style.display = name.includes(q) ? '' : 'none';
      });
    };
  }

  // File drop zones on collection tiles
  container.querySelectorAll('.collection-tile:not(.ghost-tile)').forEach(tile => {
    const gid = tile.dataset.id;
    setupFileDropZone(tile, (files) => enqueueFiles(files, { groupId: gid }));
    setupItemDropTarget(tile, async (itemId) => {
      try {
        await post(`/groups/${gid}/items`, { item_id: itemId });
        const gName = tile.querySelector('.item-name')?.textContent || 'collection';
        toast(`Added to ${gName}`, 'success');
        renderItemsList(container);
      } catch (err) { toast(err.message); }
    });
  });

  // Ghost tile drop zone — creates new collection
  const ghostTile = container.querySelector('.ghost-tile');
  if (ghostTile) {
    setupFileDropZone(ghostTile, (files) => {
      const autoName = generateAutoName();
      enqueueFiles(files, { createGroup: true, autoGroupName: autoName });
      // Override complete callback to include inline rename
      clearCompleteCallbacks();
      onAllComplete(() => {
        if (!window.location.hash.startsWith('#/items')) return;
        renderItemsList(container);
        setTimeout(() => triggerInlineRename(getLastCreatedGroupId()), 200);
      });
    });
    ghostTile.onclick = () => createNewCollection(container);
  }

  // Refresh when uploads complete (for regular collection drops)
  clearCompleteCallbacks();
  onAllComplete(() => {
    if (window.location.hash.includes('view=collections')) renderItemsList(container);
  });

  // Collection tile clicks
  container.querySelectorAll('.collection-tile:not(.ghost-tile)').forEach(tile => {
    tile.onclick = (e) => {
      const actionBtn = e.target.closest('[data-action]');
      if (actionBtn) {
        e.stopPropagation();
        const action = actionBtn.dataset.action;
        const id = actionBtn.dataset.id;
        if (action === 'share') navigate(`#/grants/new?group_id=${id}`);
        if (action === 'delete') deleteCollection(id, container);
        return;
      }
      navigate(`#/items?view=collections&group=${tile.dataset.id}`);
    };
  });
}

// ── Drill-in View ──

function renderDrillIn(container, groupId, groupsDisplay, displayItems, groupItemsMap, itemGroupsMap) {
  const group = groupsDisplay.find(g => g.id === groupId);
  if (!group) {
    container.innerHTML = '<div class="msg msg-error">Collection not found</div>';
    return;
  }

  setTopbar(group.name, '');

  const groupItemIds = new Set(groupItemsMap[groupId] || []);
  const collectionItems = displayItems.filter(i => groupItemIds.has(i.id));

  let html = `<div class="drill-in-header">
    <button class="btn btn-ghost btn-sm" id="drill-back"><i data-lucide="arrow-left" style="width:16px;height:16px"></i> Collections</button>
    <div style="display:flex;gap:8px;align-items:center">
      <span class="badge badge-encrypted">${collectionItems.length} item${collectionItems.length !== 1 ? 's' : ''}</span>
      <button class="btn btn-secondary btn-sm" id="drill-rename"><i data-lucide="pencil" style="width:14px;height:14px"></i> Rename</button>
      <button class="btn btn-secondary btn-sm" id="drill-share"><i data-lucide="share-2" style="width:14px;height:14px"></i> Share</button>
      <button class="btn btn-danger btn-sm" id="drill-delete"><i data-lucide="trash-2" style="width:14px;height:14px"></i></button>
    </div>
  </div>`;

  html += '<div class="items-grid" id="drill-items-grid">';
  collectionItems.forEach(item => {
    html += renderItemCard(item);
  });
  html += `
    <div class="card item-card ghost-tile" id="drill-upload-ghost">
      <div class="collection-icon ghost"><i data-lucide="plus" style="width:28px;height:28px"></i></div>
      <div class="item-name drop-desktop-hint">Drop files here</div>
      <label for="drill-ghost-file-input" class="item-name drop-mobile-btn" style="display:none;cursor:pointer">Add Files</label>
      <input type="file" id="drill-ghost-file-input" multiple style="display:none">
    </div>`;
  html += '</div>';

  container.innerHTML = html;
  lucideIcons();

  // File drop zone on drill-in container
  setupFileDropZone(container, (files) => enqueueFiles(files, { groupId }));

  // Ghost tile — file upload on click/tap
  const drillGhost = document.getElementById('drill-upload-ghost');
  const drillGhostInput = document.getElementById('drill-ghost-file-input');
  if (drillGhost && drillGhostInput) {
    setupFileDropZone(drillGhost, (files) => enqueueFiles(files, { groupId }));
    drillGhost.addEventListener('click', (e) => {
      if (e.target === drillGhostInput || e.target.closest('.drop-mobile-btn')) return;
      drillGhostInput.click();
    });
    drillGhostInput.addEventListener('change', () => {
      if (drillGhostInput.files.length) enqueueFiles(drillGhostInput.files, { groupId });
    });
  }

  // Make item cards draggable
  container.querySelectorAll('.item-card:not(.ghost-tile)').forEach(card => {
    setupItemDragSource(card, card.dataset.id);
  });

  // Refresh when uploads complete
  clearCompleteCallbacks();
  onAllComplete(() => {
    if (window.location.hash.includes(`group=${groupId}`)) renderItemsList(container);
  });

  // Back
  document.getElementById('drill-back').onclick = () => navigate('#/items?view=collections');

  // Rename
  document.getElementById('drill-rename').onclick = async () => {
    const newName = await promptDialog('New collection name:', { defaultValue: group.name });
    if (!newName || newName === group.name) return;
    try {
      const encrypted = encryptItem(JSON.stringify({ name: newName }));
      await put(`/groups/${groupId}`, {
        encrypted_blob: encrypted.encrypted_blob,
        wrapped_key: encrypted.wrapped_key,
        nonce: encrypted.nonce,
      });
      renderItemsList(container);
    } catch (err) { toast(err.message); }
  };

  // Share
  document.getElementById('drill-share').onclick = () => navigate(`#/grants/new?group_id=${groupId}`);

  // Delete
  document.getElementById('drill-delete').onclick = async () => {
    if (!await confirmDialog('Delete this collection? Items will not be deleted.', { danger: true })) return;
    try {
      await del(`/groups/${groupId}`);
      navigate('#/items?view=collections');
    } catch (err) { toast(err.message); }
  };

  // Item card clicks
  container.querySelectorAll('.item-card:not(.ghost-tile)').forEach(card => {
    card.onclick = (e) => {
      const actionBtn = e.target.closest('[data-action]');
      if (actionBtn) {
        e.stopPropagation();
        const action = actionBtn.dataset.action;
        const id = actionBtn.dataset.id;
        if (action === 'share') navigate(`#/grants/new?item_id=${id}`);
        if (action === 'delete') deleteItem(id, container);
        return;
      }
      navigate(`#/items/${card.dataset.id}`);
    };
  });
}

// ── Collection Picker (for multi-select) ──

async function showCollectionPicker(itemIds, groupsDisplay, container) {
  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'dialog-overlay';
    overlay.innerHTML = `
      <div class="dialog-box" style="text-align:left;max-width:360px">
        <p class="dialog-message" style="text-align:center">Add ${itemIds.length} item${itemIds.length !== 1 ? 's' : ''} to collection</p>
        <div style="max-height:200px;overflow-y:auto;margin-bottom:12px">
          ${groupsDisplay.map(g => `
            <label style="display:flex;align-items:center;gap:10px;padding:8px 4px;cursor:pointer;font-weight:normal">
              <input type="radio" name="pick-group" value="${g.id}">
              <i data-lucide="folder" style="width:16px;height:16px;color:var(--accent)"></i>
              <span>${esc(g.name)}</span>
            </label>
          `).join('')}
          ${!groupsDisplay.length ? '<p style="color:var(--text-secondary);padding:8px">No collections yet.</p>' : ''}
        </div>
        <div class="dialog-actions" style="justify-content:space-between">
          <button class="btn btn-ghost btn-sm" id="picker-new">+ New Collection</button>
          <div style="display:flex;gap:8px">
            <button class="btn btn-secondary btn-sm" id="picker-cancel">Cancel</button>
            <button class="btn btn-primary btn-sm" id="picker-add">Add</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    try { lucide.createIcons({ nodes: [overlay] }); } catch (_) {}
    requestAnimationFrame(() => overlay.classList.add('visible'));

    const close = () => {
      overlay.classList.remove('visible');
      overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
      setTimeout(() => overlay.remove(), 300);
      resolve();
    };

    overlay.querySelector('#picker-cancel').onclick = close;
    overlay.querySelector('#picker-add').onclick = async () => {
      const selected = overlay.querySelector('input[name="pick-group"]:checked');
      if (!selected) { toast('Select a collection'); return; }
      const gid = selected.value;
      try {
        for (const itemId of itemIds) {
          await post(`/groups/${gid}/items`, { item_id: itemId });
        }
        toast(`Added ${itemIds.length} item${itemIds.length !== 1 ? 's' : ''} to collection`, 'success');
        close();
        renderItemsList(container);
      } catch (err) { toast(err.message); }
    };
    overlay.querySelector('#picker-new').onclick = async () => {
      close();
      const gid = await createNewCollection(container);
      if (gid) {
        try {
          for (const itemId of itemIds) {
            await post(`/groups/${gid}/items`, { item_id: itemId });
          }
          toast(`Added ${itemIds.length} item${itemIds.length !== 1 ? 's' : ''} to collection`, 'success');
          renderItemsList(container);
        } catch (err) { toast(err.message); }
      }
    };
  });
}

async function createNewCollection(container) {
  const name = await promptDialog('Collection name:');
  if (!name) return null;
  try {
    const encrypted = encryptItem(JSON.stringify({ name }));
    const group = await post('/groups', {
      encrypted_blob: encrypted.encrypted_blob,
      wrapped_key: encrypted.wrapped_key,
      nonce: encrypted.nonce,
    });
    toast('Collection created', 'success');
    navigate(`#/items?view=collections&group=${group.id}`);
    return group.id;
  } catch (err) {
    toast(err.message);
    return null;
  }
}

// ── Auto-name + Inline Rename ──

function generateAutoName() {
  const d = new Date();
  const date = d.toISOString().slice(0, 10);
  const seq = String(d.getHours() * 60 + d.getMinutes()).padStart(3, '0');
  return `${date}-${seq}`;
}

function triggerInlineRename(groupId) {
  if (!groupId) return;
  const tile = document.querySelector(`.collection-tile[data-id="${groupId}"]`);
  if (!tile) return;
  const nameEl = tile.querySelector('.item-name');
  if (!nameEl) return;
  const currentName = nameEl.textContent;
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'inline-rename-input';
  input.value = currentName;
  nameEl.textContent = '';
  nameEl.appendChild(input);
  input.focus();
  input.select();

  const commit = async () => {
    const newName = input.value.trim() || currentName;
    nameEl.textContent = newName;
    if (newName !== currentName) {
      try {
        const encrypted = encryptItem(JSON.stringify({ name: newName }));
        await put(`/groups/${groupId}`, {
          encrypted_blob: encrypted.encrypted_blob,
          wrapped_key: encrypted.wrapped_key,
          nonce: encrypted.nonce,
        });
      } catch (err) { toast(err.message); }
    }
  };

  input.addEventListener('blur', commit, { once: true });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
    if (e.key === 'Escape') { input.value = currentName; input.blur(); }
  });
}

// ── Shared Helpers ──

async function deleteItem(id, container) {
  if (!await confirmDialog('Delete this item?', { danger: true })) return;
  try {
    await del(`/items/${id}`);
    renderItemsList(container);
  } catch (err) {
    toast(err.message);
  }
}

async function deleteCollection(id, container) {
  if (!await confirmDialog('Delete this collection? Items will not be deleted.', { danger: true })) return;
  try {
    await del(`/groups/${id}`);
    renderItemsList(container);
  } catch (err) {
    toast(err.message);
  }
}
